/**
 * QuizAttemptHistory — compact list of a student's prior attempts for
 * a single quiz. Shown inline above the "Start assessment" button so
 * the student can see their progression without leaving the week view.
 *
 * States
 * ------
 * - **loading** — 2 skeleton rows
 * - **empty** — no row (caller decides whether to render anything)
 * - **list** — chronological newest-first, with score + pass/fail pill
 */

import { useCallback, useEffect, useState } from 'react';
import { CheckCircle2, Clock, XCircle } from 'lucide-react';
import { Skeleton } from '../Skeleton';
import {
    getQuizAttempts,
    type BootcampQuizAttempt,
} from '../../bootcampService';
import './QuizAttemptHistory.css';

type Props = {
    quizId: string;
    /** Bumps on every external change (e.g. after the student finishes
     *  a new attempt) to force a refetch without remounting. */
    refreshKey?: number;
};

function QuizAttemptHistory({ quizId, refreshKey = 0 }: Props) {
    const [attempts, setAttempts] = useState<BootcampQuizAttempt[] | null>(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const rows = await getQuizAttempts(quizId);
            // Sort newest-first (API order isn't guaranteed).
            rows.sort((a, b) => {
                const ta = a.started_at ? Date.parse(a.started_at) : 0;
                const tb = b.started_at ? Date.parse(b.started_at) : 0;
                return tb - ta;
            });
            setAttempts(rows);
        } catch (err) {
            console.error('[QuizAttemptHistory] load failed', err);
            setAttempts([]);
        } finally {
            setLoading(false);
        }
    }, [quizId]);

    useEffect(() => {
        void load();
    }, [load, refreshKey]);

    if (loading) {
        return (
            <div className="quiz-history">
                <Skeleton width="100%" height="32px" borderRadius="8px" />
                <Skeleton width="100%" height="32px" borderRadius="8px" />
            </div>
        );
    }

    if (!attempts || attempts.length === 0) {
        return null; // caller decides empty state
    }

    const bestScore = Math.max(...attempts.map((a) => a.score));
    const everPassed = attempts.some((a) => a.passed);

    return (
        <div className="quiz-history">
            <header className="quiz-history__head">
                <h4>Your attempts ({attempts.length})</h4>
                <span className={`quiz-history__best${everPassed ? ' is-pass' : ''}`}>
                    Best score: {bestScore}%
                </span>
            </header>
            <ul className="quiz-history__list">
                {attempts.map((attempt) => {
                    const label = attempt.passed ? 'Passed' : 'Try again';
                    const Icon = attempt.passed ? CheckCircle2 : XCircle;
                    return (
                        <li
                            key={attempt.id}
                            className={`quiz-history__row${attempt.passed ? ' is-pass' : ' is-fail'}`}
                        >
                            <span className="quiz-history__row-score">{attempt.score}%</span>
                            <span className="quiz-history__row-status" aria-label={label}>
                                <Icon size={14} aria-hidden="true" />
                                {label}
                            </span>
                            <time className="quiz-history__row-time">
                                <Clock size={12} aria-hidden="true" />
                                {formatDate(attempt.started_at)}
                            </time>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}

function formatDate(iso: string): string {
    try {
        const d = new Date(iso);
        return d.toLocaleDateString(undefined, {
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
        });
    } catch {
        return '—';
    }
}

export default QuizAttemptHistory;
